import Modal from '..';
import { showMigrationModal } from 'redux/mode/actions';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Loader } from 'components/Widget/Loader';
import forge from 'apis/forge';
import { displayAlertModal } from 'redux/mode/util';
import * as routes from 'routes';

interface MigrationModalProps {
	dsId: string;
	context: 'editor' | 'scene';
	sceneId?: string;
}

const MigrationModal = ({ dsId, context, sceneId }: MigrationModalProps) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [migrating, setMigrating] = useState(false);

	const close = () => {
		dispatch(showMigrationModal(null));
	};

	const handleMigrate = async () => {
		setMigrating(true);
		try {
			await forge.migrateDesignSystem({ designSystemId: dsId });
			// Migration started - polling will detect completion and load content
			// Keep modal open to show progress
		} catch (error: any) {
			console.error('Migration failed:', error);
			close();
			displayAlertModal({
				title: 'Update Failed',
				description:
					"We couldn't update this design system. Please contact support@devign.com for assistance.",
				submitLabel: 'OK',
				onSubmit: () => {},
			});
		}
	};

	const handleUnlink = async () => {
		if (!sceneId) return;
		try {
			await forge.linkSceneDesignSystem({
				sceneId,
				designSystemId: null,
			});
			close();
		} catch (error) {
			console.error('Failed to unlink design system:', error);
			displayAlertModal({
				title: 'Failed to Unlink',
				description: 'Failed to unlink design system. Please try again.',
				submitLabel: 'OK',
				onSubmit: () => {},
			});
		}
	};

	const handleNewProject = () => {
		close();
		navigate('/');
	};

	const handleNewDesignSystem = () => {
		close();
		navigate(routes.createDesignSystem());
	};

	// Migrating state - show spinner and message
	if (migrating) {
		return (
			<Modal
				title="Updating Design System..."
				description={
					<div style={{ textAlign: 'center', padding: '20px 0' }}>
						<div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
							<Loader width={48} height={48} />
						</div>
						<p style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
							This may take up to 5 minutes. Don't close this window. Don't click anywhere else.
							Wait for this to complete.
						</p>
					</div>
				}
				onClickOutside={(e) => {
					e.preventDefault(); // Prevent closing during migration
				}}
			/>
		);
	}

	// Prompt state - show action buttons
	if (context === 'editor') {
		return (
			<Modal
				title="Update Design System Required"
				description="This design system uses an older format that needs to be updated. This process takes about 5 minutes."
				cancelLabel="Maybe later"
				submitLabel="Update Now"
				onCancel={handleNewDesignSystem}
				onSubmit={handleMigrate}
				onClickOutside={(e) => {
					e.preventDefault(); // Prevent closing - user must choose
				}}
			/>
		);
	}

	// Scene context - Unlink only (no more upgrades from scenes)
	return (
		<Modal
			title="Old Design System Detected"
			description="This project uses an older design system format that can no longer be used with projects. Please unlink it and convert it separately in the design system editor if needed."
			submitLabel="Unlink DS"
			altActionLabel="Maybe later"
			onSubmit={handleUnlink}
			onAltAction={handleNewProject}
			onClickOutside={(e) => {
				e.preventDefault(); // Prevent closing - user must choose
			}}
		/>
	);
};

export default MigrationModal;
